import React, { useState, useRef, useEffect } from 'react';

const RouletteWheel = ({ 
  prizes = [], 
  onSpinComplete, 
  isSpinning, 
  setIsSpinning,
  mode = 'automatic',
  triggerSpin = false,
  onSpinStart,
  primaryColor = '#0d9488',
  secondaryColor = '#2dd4bf'
}) => {
  const [rotation, setRotation] = useState(0);
  const wheelRef = useRef(null);
  const animationRef = useRef(null);

  // Alternating segment color pairs
  const segmentColors = [
    '#0d9488',
    '#E8192C',
    '#0d9488',
    '#E8192C',
    '#0d9488',
    '#E8192C',
    '#0d9488',
    '#E8192C',
    '#0d9488',
    '#E8192C',
  ];

  const getRandomPrize = () => {
    const totalProbability = prizes?.reduce((sum, prize) => sum + (prize?.probability || 1), 0);
    let random = Math.random() * totalProbability;
    for (let prize of prizes) {
      random -= (prize?.probability || 1);
      if (random <= 0) return prize;
    }
    return prizes?.[0];
  };

  const spinWheel = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    if (onSpinStart) onSpinStart();

    const selectedPrize = getRandomPrize();
    const prizeIndex = prizes?.findIndex(p => p?.id === selectedPrize?.id);
    const segmentAngle = 360 / prizes?.length;
    const targetAngle = 360 - (prizeIndex * segmentAngle + segmentAngle / 2);
    const spins = 5 + Math.random() * 3;
    const finalRotation = rotation + (360 * spins) + targetAngle;

    let startTime = null;
    const duration = 4000 + Math.random() * 2000;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 4);
      const currentRotation = rotation + (finalRotation - rotation) * easeOut;
      setRotation(currentRotation);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setRotation(finalRotation % 360);
        setIsSpinning(false);
        setTimeout(() => {
          onSpinComplete(selectedPrize);
        }, 500);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (mode === 'assisted' && triggerSpin && !isSpinning) {
      spinWheel();
    }
  }, [triggerSpin, mode]);

  const handleClick = () => {
    if (!isSpinning) spinWheel();
  };

  useEffect(() => {
    return () => {
      if (animationRef?.current) cancelAnimationFrame(animationRef?.current);
    };
  }, []);

  const segmentAngle = 360 / prizes?.length;

  // Split prize name into lines that fit within segment
  const getTextLines = (name) => {
    const words = name?.split(' ') || [];
    if (words?.length <= 2) return words?.length === 1 ? [name] : [words?.[0], words?.slice(1)?.join(' ')];
    // Group into lines of max 2 words
    const lines = [];
    for (let i = 0; i < words?.length; i += 2) {
      lines?.push(words?.slice(i, i + 2)?.join(' '));
    }
    return lines;
  };

  return (
    <div
      className="relative mx-auto"
      style={{ width: 'min(85vw, 85vh, 800px)', maxWidth: '800px' }}
    >
      <div className="relative" style={{ aspectRatio: '1 / 1' }}>
        {/* Red Triangular Pointer at Top */}
        <div
          className="absolute left-1/2 z-30"
          style={{ top: 0, transform: 'translateX(-50%) translateY(-20%)' }}
        >
          <div
            className="w-0 h-0"
            style={{
              borderLeft: 'clamp(14px, 2.5vw, 36px) solid transparent',
              borderRight: 'clamp(14px, 2.5vw, 36px) solid transparent',
              borderTop: 'clamp(28px, 5vw, 72px) solid #DC2626',
              filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))'
            }}
          />
        </div>

        {/* Outer Golden Ring with LED Lights */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #D4AF37 0%, #F4E5A1 25%, #D4AF37 50%, #B8941F 75%, #D4AF37 100%)',
            padding: 'clamp(6px, 1.2vw, 18px)',
            boxShadow: '0 0 40px rgba(212, 175, 55, 0.6), inset 0 0 20px rgba(0,0,0,0.3)'
          }}
        >
          {/* LED Light Bulbs */}
          {[...Array(24)]?.map((_, i) => {
            const angle = (i * 360 / 24) * (Math.PI / 180);
            const radius = 48;
            const x = 50 + radius * Math.cos(angle - Math.PI / 2);
            const y = 50 + radius * Math.sin(angle - Math.PI / 2);
            return (
              <div
                key={i}
                className="absolute rounded-full animate-pulse"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: 'clamp(6px, 1vw, 16px)',
                  height: 'clamp(6px, 1vw, 16px)',
                  backgroundColor: '#FFF',
                  boxShadow: '0 0 10px #FFF, 0 0 20px #FFD700',
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: '1.5s'
                }}
              />
            );
          })}

          {/* Inner wheel container */}
          <div
            className="relative w-full h-full rounded-full overflow-hidden"
            style={{ boxShadow: 'inset 0 0 30px rgba(0,0,0,0.5)' }}
          >
            {/* Rotating Wheel */}
            <div
              ref={wheelRef}
              className="absolute inset-0 rounded-full cursor-pointer touch-manipulation"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: isSpinning ? 'none' : 'transform 0.3s ease-out'
              }}
              onClick={handleClick}
            >
              <svg viewBox="0 0 400 400" className="w-full h-full">
                {prizes?.map((prize, index) => {
                  const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
                  const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);

                  const x1 = 200 + 200 * Math.cos(startAngle);
                  const y1 = 200 + 200 * Math.sin(startAngle);
                  const x2 = 200 + 200 * Math.cos(endAngle);
                  const y2 = 200 + 200 * Math.sin(endAngle);

                  const largeArcFlag = segmentAngle > 180 ? 1 : 0;
                  const pathData = `M 200 200 L ${x1} ${y1} A 200 200 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                  const midAngle = startAngle + (endAngle - startAngle) / 2;
                  // Text positioned at ~60% radius from center
                  const textRadius = 115;
                  const textX = 200 + textRadius * Math.cos(midAngle);
                  const textY = 200 + textRadius * Math.sin(midAngle);
                  const textRotation = (midAngle * 180 / Math.PI) + 90;

                  const fillColor = segmentColors?.[index % segmentColors?.length];
                  const lines = getTextLines(prize?.name);
                  const lineHeight = 15;
                  const totalHeight = (lines?.length - 1) * lineHeight;

                  return (
                    <g key={prize?.id}>
                      <path
                        d={pathData}
                        fill={fillColor}
                        stroke="#D4AF37"
                        strokeWidth="2"
                      />
                      {/* Multi-line text */}
                      {lines?.map((line, li) => (
                        <text
                          key={li}
                          x={textX}
                          y={textY - totalHeight / 2 + li * lineHeight}
                          fill="white"
                          fontSize="12"
                          fontWeight="800"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          transform={`rotate(${textRotation} ${textX} ${textY})`}
                          style={{
                            textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                            letterSpacing: '-0.3px'
                          }}
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* Center Button */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative flex items-center justify-center">
                {/* Outer golden ring */}
                <div
                  className="absolute rounded-full"
                  style={{
                    width: 'clamp(90px, 16vw, 200px)',
                    height: 'clamp(90px, 16vw, 200px)',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #F4E5A1 50%, #D4AF37 100%)',
                    boxShadow: '0 0 30px rgba(212, 175, 55, 0.8)'
                  }}
                />
                {/* Blue center button */}
                <button
                  onClick={handleClick}
                  disabled={isSpinning}
                  className="relative rounded-full flex flex-col items-center justify-center pointer-events-auto transition-transform active:scale-95 disabled:opacity-70 touch-manipulation"
                  style={{
                    width: 'clamp(82px, 14.5vw, 184px)',
                    height: 'clamp(82px, 14.5vw, 184px)',
                    background: `linear-gradient(135deg, ${secondaryColor || '#2dd4bf'} 0%, ${primaryColor || '#0d9488'} 100%)`,
                    boxShadow: '0 4px 20px rgba(13, 148, 136, 0.6), inset 0 2px 10px rgba(255,255,255,0.3)',
                    border: 'clamp(3px, 0.5vw, 6px) solid #D4AF37',
                    zIndex: 1
                  }}
                >
                  <span
                    className="text-white font-black leading-tight text-center"
                    style={{
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      fontSize: 'clamp(12px, 2.2vw, 30px)'
                    }}
                  >
                    ¡GIRA<br/>AQUI!
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouletteWheel;